import { z } from 'zod';
import {
  entityIdSchema,
  interestTopicSchema,
  isoDateTimeSchema,
  localeSchema,
} from '@ebu/contracts';
import { localIdSchema } from '@ebu/world-schema';

/**
 * Versioned IndexedDB layout for child-owned local data (spec: Local-only learner entities).
 * Nothing here is ever sent to the server. Object-store changes are append-only upgrade steps:
 * never edit a shipped step; add the next one and bump LOCAL_DB_VERSION. Record contents are
 * validated with the schemas below on every read and write.
 */

export const LOCAL_DB_NAME = 'ebu-kids-world-local';
export const LOCAL_DB_VERSION = 1;

export const STORES = {
  workspace: 'guest_workspace',
  project: 'world_project',
  attempt: 'challenge_attempt',
  evidence: 'skill_evidence',
} as const;

type UpgradeStep = (db: IDBDatabase, tx: IDBTransaction) => void;

/** Keyed by the version the step upgrades TO. */
export const UPGRADE_STEPS: Readonly<Record<number, UpgradeStep>> = {
  1: (db) => {
    db.createObjectStore(STORES.workspace, { keyPath: 'id' });
    db.createObjectStore(STORES.project, { keyPath: 'id' }).createIndex(
      'by-workspace',
      'workspaceId',
    );
    // Record schemas for attempts/evidence arrive with the adaptive engine (Phase 7); the stores exist
    // now so "Clear my data" and workspace isolation cover them from the start.
    db.createObjectStore(STORES.attempt, { keyPath: 'id' }).createIndex(
      'by-workspace',
      'workspaceId',
    );
    db.createObjectStore(STORES.evidence, { keyPath: 'id' }).createIndex(
      'by-workspace',
      'workspaceId',
    );
  },
};

export function applyUpgrades(
  db: IDBDatabase,
  tx: IDBTransaction,
  oldVersion: number,
  newVersion: number,
  steps: Readonly<Record<number, UpgradeStep>> = UPGRADE_STEPS,
): void {
  for (let v = oldVersion + 1; v <= newVersion; v++) {
    const step = steps[v];
    if (!step) throw new Error(`Missing local database upgrade step to v${v}`);
    step(db, tx);
  }
}

export const workspaceIdSchema = z.string().regex(/^[A-Za-z0-9_-]{8,64}$/);

/** A guest workspace, or (Phase 9) a locally cloned fictional sample learner. Never a real account. */
export const workspaceRecordSchema = z.strictObject({
  id: workspaceIdSchema,
  kind: z.enum(['guest', 'sample-learner']),
  sampleProfileId: entityIdSchema.nullable(),
  createdAt: isoDateTimeSchema,
  locale: localeSchema,
  /** Stored only when the child explicitly opts in; null otherwise. */
  interests: z.array(interestTopicSchema).nullable(),
  /**
   * Selected broadcaster configuration (Phase 4). A record field, not an object-store change, so no
   * upgrade step: records written before Phase 4 read as null ("not chosen yet").
   */
  broadcasterId: entityIdSchema.nullable().default(null),
  /** "world" = 3D island, "map" = simple 2D map (low-graphics mode). Same defaulting as above. */
  graphics: z.enum(['world', 'map']).default('world'),
  /**
   * Phase 7: null = not asked yet, false = "No thanks" (progress stays in memory), true = save
   * challenge attempts and evidence on this browser. Record field with a default: no upgrade step.
   */
  saveProgress: z.boolean().nullable().default(null),
  /** Where the child chose to start the circuit challenges (a choice, never evidence). */
  startChoice: z.enum(['beginning', 'harder']).nullable().default(null),
  /**
   * Phase 9, guest record only: the fictional sample learner workspace now in use (null = the
   * guest's own). Record field with a default: no upgrade step.
   */
  activeSampleId: workspaceIdSchema.nullable().default(null),
  /**
   * Owner direction 2026-11-04 (ADR 0017): who is playing — "little" (3–8) or "bigger" (9+); null =
   * not chosen yet. No birth date. Record field with a default: no upgrade step.
   */
  ageMode: z.enum(['little', 'bigger']).nullable().default(null),
  /**
   * Screen time set by a grown-up: minutes allowed per day (null = no limit) and today's use on
   * this browser. `day` is the local calendar date (YYYY-MM-DD); a new day starts at zero.
   */
  screenTime: z
    .strictObject({
      limitMinutes: z.int().min(1).max(600).nullable(),
      day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      usedSeconds: z.int().min(0).max(86_400),
    })
    .nullable()
    .default(null),
});
export type WorkspaceRecord = z.infer<typeof workspaceRecordSchema>;

/** Envelope around a world document; `world` is validated separately by @ebu/world-schema. */
export const projectRecordSchema = z.strictObject({
  id: localIdSchema,
  workspaceId: workspaceIdSchema,
  updatedAt: isoDateTimeSchema,
  world: z.unknown(),
});
export type ProjectRecord = z.infer<typeof projectRecordSchema>;

const challengeIdSchema = entityIdSchema;

/**
 * One finished challenge attempt (spec `challenge_attempt`): only what is needed to explain the
 * evidence — no interaction trace, no timing of clicks, nothing personal.
 */
export const attemptRecordSchema = z.strictObject({
  id: z.string().regex(/^[A-Za-z0-9_-]{8,80}$/),
  workspaceId: workspaceIdSchema,
  missionId: entityIdSchema,
  challengeId: challengeIdSchema,
  challengeVersion: z.int().min(1),
  objectiveId: z.string().max(80),
  level: z.int().min(1).max(3),
  outcome: z.enum(['solved', 'not-yet']),
  tests: z.int().min(0).max(1000),
  hintsUsed: z.int().min(0).max(10),
  showMeUsed: z.boolean(),
  /** True when the child picked this challenge instead of the suggested one. */
  chosenByChild: z.boolean(),
  endedAt: isoDateTimeSchema,
});
export type AttemptStoreRecord = z.infer<typeof attemptRecordSchema>;

/**
 * Current evidence per objective (spec `skill_evidence`), recomputed from the attempts after each
 * one and kept for the portfolio (Phase 9). Always provisional; the attempts are the source.
 */
export const evidenceRecordSchema = z.strictObject({
  id: z.string().max(160),
  workspaceId: workspaceIdSchema,
  objectiveId: z.string().max(80),
  source: z.literal('circuit-game'),
  status: z.enum(['unobserved', 'emerging', 'practised', 'demonstrated']),
  reason: z.string().max(60),
  keyAttemptId: z.string().max(80).nullable(),
  attemptCount: z.int().min(0),
  provisional: z.literal(true),
  updatedAt: isoDateTimeSchema,
});
export type EvidenceStoreRecord = z.infer<typeof evidenceRecordSchema>;
